<template>
  <div v-if="isReady" class="app-wrapper min-h-screen flex flex-col">
    <!-- Add DynamicDialogs at the root level -->
    <DynamicDialogs />
    <main class="flex-1 overflow-y-auto bg-[var(--background-color)]">
      <router-view v-if="auth.isInitialized && !isLoading" v-slot="{ Component }">
        <template v-if="isLoginView">
          <component :is="Component" class="pt-2"/>
        </template>
        <transition v-else name="fade" mode="out-in">
          <component :is="Component"/>
        </transition>
      </router-view>
      <div v-else class="loading-container">
        <div class="loading-spinner"></div>
      </div>
    </main>
  </div>
  <div v-else class="loading-container">
    <div class="loading-spinner"></div>
  </div>

  <!-- Global notification snackbar -->
  <Transition name="fade">
    <div v-if="snackbarStore.isVisible" class="snackbar" :class="snackbarStore.type">
      <div class="snackbar-icon">
        <i v-if="snackbarStore.type === 'success'" class="fas fa-check-circle"></i>
        <i v-if="snackbarStore.type === 'error'" class="fas fa-exclamation-circle"></i>
        <i v-if="snackbarStore.type === 'warning'" class="fas fa-exclamation-triangle"></i>
        <i v-if="snackbarStore.type === 'info'" class="fas fa-info-circle"></i>
      </div>
      {{ snackbarStore.message }}
      <button @click="snackbarStore.hide()" class="close-btn">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { onBeforeMount, ref, watch, onMounted, computed, onUnmounted } from 'vue'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { useAuthStore } from '@/stores/auth'
  import { useThemeStore } from '@/stores/theme'
  import { useRouter } from 'vue-router'
  import DynamicDialogs from '@/components/ui/modal/DynamicDialogs.vue'
  import { useDialog } from '@/composables/useDialog'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'

  // Declare global property for JWT login loading
  declare global {
    interface Window {
      jwtLoginLoading?: boolean
    }
  }

  // Store initialization
  const snackbarStore = useSnackbarStore()
  const auth = useAuthStore()
  const themeStore = useThemeStore()
  const router = useRouter()
  const dialog = useDialog()
  const { init: initFeatureFlags, getFlag, hasFeature, getValue, onChange } = useFeatureFlag()

  const crispInitialized = ref(false)
  const crispFlagEnabled = ref(false)
  const crispEnableAllEnabled = ref(false)
  const crispEnableAll = ref(false)
  const crispFlagEmails = ref<string[]>([])

  const evaluateCrispGate = () => {
    try {
      const userEmail = (auth.user?.email || '').toLowerCase()

      if (crispEnableAllEnabled.value && crispEnableAll.value) {
        initCrisp(auth.user?.email)
        return
      }

      if (crispFlagEnabled.value && userEmail && crispFlagEmails.value.includes(userEmail)) {
        initCrisp(auth.user?.email)
        return
      }

      const w = window as any
      if (w.$crisp) {
        (w.$crisp = w.$crisp || []).push(["do", "session:reset"])
        ;(w.$crisp = w.$crisp || []).push(["do", "chat:hide"])
      }
    } catch (_) { /* ignore */ }
  }
  const initCrisp = (email?: string) => {
    if (crispInitialized.value) {
      // Update email if provided after initialization
      try {
        if (email) {
          ;((window as any).$crisp = (window as any).$crisp || []).push(['set','user:email', email])
        }
        ;((window as any).$crisp = (window as any).$crisp || []).push(['do','chat:show'])
      } catch (_) { /* ignore */ }
      return
    }
    try {
      ;(function(){
        (window as any).$crisp = (window as any).$crisp || []
        ;(window as any).CRISP_WEBSITE_ID = '669c9301-24a8-4c57-80c2-b5add5834076'
        const d = document
        const s = d.createElement('script')
        s.src = 'https://client.crisp.chat/l.js'
        s.async = true
        s.onload = () => {
          try {
            if (email) {
              ;((window as any).$crisp = (window as any).$crisp || []).push(['set','user:email', email])
            }
            ;((window as any).$crisp = (window as any).$crisp || []).push(['do','chat:show'])
          } catch (_) { /* ignore */ }
        }
        d.getElementsByTagName('head')[0].appendChild(s)
      })()
      crispInitialized.value = true
    } catch (_) { /* ignore */ }
  }

  // Track states
  const isReady = ref(false)
  const isLoading = ref(true)
  const isLoginView = computed(() => {
    const path = router.currentRoute.value.path
    return path === '/login' ||
           path === '/forgot-password' ||
           path === '/register'
  })

  // Update body class based on route
  watch(() => router.currentRoute.value.path, (path) => {
    if (path === '/login') {
      document.body.classList.add('login-page')
      document.body.classList.remove('forgot-password-page', 'register-page')
    } else if (path === '/forgot-password') {
      document.body.classList.add('forgot-password-page')
      document.body.classList.remove('login-page', 'register-page')
    } else if (path === '/register') {
      document.body.classList.add('register-page')
      document.body.classList.remove('login-page', 'forgot-password-page')
    } else {
      document.body.classList.remove('login-page', 'forgot-password-page', 'register-page')
    }
  }, { immediate: true })

  // Dropdown states
  const isCompanyDropdownOpen = ref(false)
  const isUserDropdownOpen = ref(false)
  const isMobileMenuOpen = ref(false)
  const isMobileCompanyOpen = ref(false)
  const isNewLoanDropdownOpen = ref(false)
  const isMobileNewLoanOpen = ref(false)
  const isResourcesDropdownOpen = ref(false)
  const isMobileResourcesOpen = ref(false)
  const isToolsDropdownOpen = ref(false)
  const isMobileToolsOpen = ref(false)
  const isCorporate = ref(false)

  const companyRoutes = ['/members'];
  const toolsRoutes = ['/tools/how-to-videos'];
  const newLoanRoutes = ['/loan-application/new', '/loan-application/upload'];
  const resourcesRoutes = ['/resources', '/resources/exclusionary-list', '/resources/appraisal', '/resources/programs', '/resources/forms', '/resources/bank-statement-submission', '/resources/calculators', '/resources/mortgage-clause'];

  const isCompanyActive = computed(() => companyRoutes.includes(router.currentRoute.value.path));
  const isNewLoanActive = computed(() => newLoanRoutes.includes(router.currentRoute.value.path));
  const isToolsActive = computed(() => toolsRoutes.includes(router.currentRoute.value.path));
  const isResourcesActive = computed(() => resourcesRoutes.includes(router.currentRoute.value.path));

  // Methods
  const handleLogout = async () => {
    isLoading.value = true
    try {
      await auth.logout()
      await router.push('/login')
    } finally {
      isLoading.value = false
    }
  }

  // Toggle dropdowns
  const toggleCompanyDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isCompanyDropdownOpen.value = !isCompanyDropdownOpen.value
  }

  const toggleUserDropdown = (event: Event) => {
    event.stopPropagation()
    isCompanyDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isUserDropdownOpen.value = !isUserDropdownOpen.value
  }

  const toggleMobileCompanyDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileNewLoanOpen.value = false
    isMobileCompanyOpen.value = !isMobileCompanyOpen.value
  }

  const toggleNewLoanDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isCompanyDropdownOpen.value = false
    isResourcesDropdownOpen.value = false
    isNewLoanDropdownOpen.value = !isNewLoanDropdownOpen.value
  }

  const toggleMobileNewLoanDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileCompanyOpen.value = false
    isMobileResourcesOpen.value = false
    isMobileNewLoanOpen.value = !isMobileNewLoanOpen.value
  }

  const toggleToolsDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isCompanyDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isResourcesDropdownOpen.value = false
    isToolsDropdownOpen.value = !isToolsDropdownOpen.value
  }

  const toggleMobileToolsDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileCompanyOpen.value = false
    isMobileNewLoanOpen.value = false
    isMobileResourcesOpen.value = false
    isMobileToolsOpen.value = !isMobileToolsOpen.value
  }

  const toggleResourcesDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isCompanyDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isToolsDropdownOpen.value = false
    isResourcesDropdownOpen.value = !isResourcesDropdownOpen.value
  }

  const toggleMobileResourcesDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileCompanyOpen.value = false
    isMobileNewLoanOpen.value = false
    isMobileResourcesOpen.value = !isMobileResourcesOpen.value
  }

  // Close dropdowns
  const closeAllDropdowns = () => {
    isCompanyDropdownOpen.value = false
    isUserDropdownOpen.value = false
    isMobileCompanyOpen.value = false
    isNewLoanDropdownOpen.value = false
    isMobileNewLoanOpen.value = false
    isToolsDropdownOpen.value = false
    isMobileToolsOpen.value = false
    isResourcesDropdownOpen.value = false
    isMobileResourcesOpen.value = false
  }

  const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    if (!isMobileMenuOpen.value) {
      closeAllDropdowns()
    }
  }

  const closeMobileNav = () => {
    isMobileMenuOpen.value = false
    closeAllDropdowns()
  }

  const logout = () => {
    isMobileMenuOpen.value = false
    closeAllDropdowns()
    // Small delay to ensure dropdown closes before showing confirmation
    setTimeout(() => {
      dialog.confirmation(
        'Sign Out',
        'Are you sure you want to Sign out?',
        'Confirm',
        'Cancel',
        handleLogout,
        () => {
          // Cancel action - do nothing, just close the dialog
        }
      )
    }, 100)
  }

  // Close dropdowns when clicking outside
  const handleClickOutside = (event: MouseEvent) => {
    if (!event.target) return
    
    // Close all dropdowns when clicking outside
    closeAllDropdowns()
  }

  // Close dropdowns on route change
  watch(
    () => router.currentRoute.value.path,
    () => {
      closeAllDropdowns()
      isMobileMenuOpen.value = false
    }
  )

  const handleImageError = (event: Event) => {
    const target = event.target as HTMLImageElement
    if (target) {
      target.src = ''
      target.classList.add('bg-blue-50')
      
      const parent = target.parentElement
      if (parent) {
        parent.classList.add('fallback-avatar')
      }
    }
  }

  // Initialize app
  onBeforeMount(async () => {
    try {
      // Font Awesome setup
      const fontAwesomeLink = document.createElement('link')
      fontAwesomeLink.rel = 'stylesheet'
      fontAwesomeLink.href =
        'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css'
      document.head.appendChild(fontAwesomeLink)

      // Set ready state immediately to show loading spinner
      isReady.value = true

      // Initialize auth
      await auth.initialize()

      if(auth.user?.role.toLowerCase() === "corporate") {
        isCorporate.value = true
      }
    } catch (error) {
      console.error('App initialization failed:', error)
    } finally {
      isLoading.value = false
    }
  })

  // Watch for auth initialization
  watch(
    () => auth.isInitialized,
    newValue => {
      if (newValue) {
        isLoading.value = false
      }
    },
    { immediate: true }
  )

  // Add these watchers after the existing variable declarations
  watch(
    () => auth.user,
    (newUser) => {
      if (newUser) {
        // Update corporate status whenever user changes
        isCorporate.value = (newUser as any)?.accessLevel?.toLowerCase() === "corporate"
        evaluateCrispGate()
      } else {
        isCorporate.value = false
        
        // Clear Crisp session and email when user logs out
        const w = window as any
        (w.$crisp = w.$crisp || []).push(["do", "session:reset"])
        (w.$crisp = w.$crisp || []).push(["set", "user:email", ""])
      }
    },
    { immediate: true } // This will run immediately on component creation
  )
  
  // Handle impersonation login
  const handleImpersonationLogin = async (jwtToken: string) => {
    try {

      
      // Set global loading state for JWT login
      window.jwtLoginLoading = true
      
      // Let loginWithJWT handle the logout and login process

      const response = await auth.loginWithJWT(jwtToken)
      
  
      
      // Verify that login was successful
      if (!auth.isAuthenticated || !auth.user) {
        throw new Error('Login failed - user not authenticated after loginWithJWT')
      }
      
      // Show success message
      const userName = response.user.firstName + ' ' + response.user.lastName
      snackbarStore.showSuccess(`Impersonating ${userName}`)
      
      // Clean up URL
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.delete('token')
      window.history.replaceState({}, '', newUrl.toString())
      
      // Clear the loading state
      window.jwtLoginLoading = false
      
      // Redirect to loans using Vue Router (same as regular login)
      await router.push('/loans')
      
    } catch (error: any) {
      console.error('App.vue: Impersonation login failed:', error)
      console.error('App.vue: Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status
      })
      snackbarStore.showError('Impersonation failed: ' + (error.message || 'Invalid token'))
      
      // Clear the loading state on error
      window.jwtLoginLoading = false
      
      // Clean up URL on error
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.delete('token')
      window.history.replaceState({}, '', newUrl.toString())
    }
  }

  // Add and remove click event listeners
  onMounted(async () => {
    document.addEventListener('click', handleClickOutside)
    
    try {
      const applyFlags = () => {
        
        crispEnableAllEnabled.value = !!hasFeature('crisp_enable_all')
        crispEnableAll.value = !!getValue('crisp_enable_all')
        crispFlagEnabled.value = !!hasFeature('crisp_user')

        let emails: string[] = []
        if (crispFlagEnabled.value) {
          const raw = getValue('crisp_user') as unknown as string | string[] | undefined
          if (Array.isArray(raw)) emails = raw
          else if (typeof raw === 'string') {
            try {
              const parsed = JSON.parse(raw)
              if (Array.isArray(parsed)) emails = parsed
              else if (raw.includes('@')) emails = [raw]
            } catch (_) {
              if (raw.includes('@')) emails = [raw]
            }
          }
        }
        crispFlagEmails.value = emails.map(e => (String(e || '')).trim().toLowerCase())

        evaluateCrispGate()
      }

      await initFeatureFlags()
      // Apply immediately after init to get current flags
      applyFlags()
      // Subscribe for future changes
      onChange(() => applyFlags())
    } catch (e) {
      console.error('Failed to initialize Feature Flags:', e)
    }
    // Check for JWT token in URL for impersonation
    const urlParams = new URLSearchParams(window.location.search)
    const jwtToken = urlParams.get('token')
    
    if (jwtToken) {
      // Redirect to impersonation loading page with token preserved
      router.push({
        path: '/impersonation-loading',
        query: { token: jwtToken }
      })
    }
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
</script>

<style lang="scss">
  :root {
    // Light mode variables
    --primary: #001f60;
    --primary-hover: #61a2d0;
    --ameritrust-primary: #001F60;
    --secondary: #be0b31;
    --ameritrust-secondary: #BE0B31;
    --text-color: #1a1a1a;
    --reverse-text-color: #ffffff;
    --background-color: #f8f9fa;
    --surface-ground: #f7f8f9;
    --surface-card: #ffffff;
    --surface-border: #e9ecef;
    --surface-hover: #f8f9fa;
    --text-secondary: #6c757d;
    --input-color: #fff;
    --skeleton-color: #dbdfe3;
    --skeleton-ground: #ebedef;
    --input-background: #fff;
    --card-background: #fff;
    --checkbox-color: #2d354c;
    --property-border: #e2e8f0;
    --shadow-color: rgba(0, 0, 0, 0.1);
    --has-errors-background: #f8d7da;
  }

  :root[data-theme='dark'] {
    // Dark mode variables
    --primary: #3b82f6;
    --primary-hover: #2271ef;
    --ameritrust-primary: #3b82f6;
    --secondary: #ef4444;
    --ameritrust-secondary: #ef4444;
    --text-color: #e5e7eb;
    --reverse-text-color: #1a1f2c;
    --background-color: #1a1f2c;
    --surface-ground: #1a1f2c;
    --surface-card: #232a3b;
    --surface-border: #2d364f;
    --surface-hover: #2a324a;
    --text-secondary: #9ca3af;
    --input-color: #2d354c;
    --skeleton-color: #2d354c;
    --skeleton-ground: #1a1f2c;
    --input-background: #1a1f2c;
    --card-background: #1a1f2c;
    --checkbox-color: #fff;
    --property-border: #2563EB;
    --shadow-color: rgba(0, 0, 0, 0.3);
    --has-errors-background: #291719;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell,
      'Open Sans', 'Helvetica Neue', sans-serif;
    color: var(--text-color);
    line-height: 1.6;
    margin-top: 64px;
    background-color: var(--background-color) !important;
    transition:
      background-color 0.0s ease,
      color 0.3s ease;
  }

  .app-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    padding-top: 64px; /* Height of navbar */
    margin-top: 0;
    padding-top: 0;
  }

  /* Navbar Styles - Redesigned */
  .navbar {
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    // padding: 0 1.5rem;
    padding: 2rem 6rem 0 10rem;
    padding-bottom: 1rem;
    // background-color: var(--surface-card);
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    &.login-page::before, &.forgot-password-page::before, &.register-page::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: url('./assets/images/bg.gif') no-repeat center center fixed;
      background-size: cover;
      opacity: 0.5; // Adjust this value to control darkness (lower = darker)
      z-index: -1;
    }

    &[data-theme='dark'] {
      &.login-page::before, &.forgot-password-page::before, &.register-page::before {
        opacity: 0.15; // Even darker for dark theme
        background: url('./assets/images/bg.gif') no-repeat center center fixed;
        background-size: cover;
      }
    }

    @media screen and (max-width: 1111px) {
      transition: all 0.5s ease;
      padding: 2rem 2rem 0 2rem;
      padding-bottom: 1rem;
    }
    
    @media (max-width: 768px) {
      padding: 0 1rem;
      background-color: var(--background-color);
      position: fixed; /* Make header fixed in mobile view */
      width: 100%;
      
      .logo {
        margin-top: 0;
        height: 30px;
      }
    }
  }

  .logo {
    margin-top: 1rem;
    height: 40px;
    display: flex;
    align-items: center;
    
    img {
      object-fit: cover;
      margin-left: 1rem;
      height: 60px;
      width: auto;
    }

    
    .light-logo {
      display: block;
    }
    
    .dark-logo {
      display: none;
    }
  }

  /* Desktop Navigation */
  .desktop-nav {
    display: flex;
    align-items: center;
    height: 100%;

    @media screen and (max-width: 950px) {
      display: none;
    }
  }

  .nav-item {
    height: 64px;
    padding: 0 1rem;
    display: flex;
    align-items: center;
    color: var(--text-color);
    font-weight: 500;
    text-decoration: none;
    position: relative;

    body:not(.login-page):not(.forgot-password-page):not(.register-page) & {
      transition: all 0.2s;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 3px;
      background-color: var(--primary);
      transform: scaleX(0);

      body:not(.login-page):not(.forgot-password-page) & {
        transition: transform 0.2s;
      }
    }
    
    &:hover {
      color: var(--primary);

      &::after {
        transform: scaleX(0.5);
      }
      // background-color: var(--surface-hover);
    }
    
    &.router-link-active {
      color: var(--primary);
      
      &::after {
        transform: scaleX(1);
      }
    }
  }

  /* Dropdown Navigation Items */
  .nav-item-dropdown {
    position: relative;
    height: 64px;
    display: flex;
    align-items: center;
  }

  .nav-dropdown-trigger {
    height: 52px !important;
    padding: 0 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-color);
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;
    position: relative;
    transition: all 0.2s;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 3px;
      background-color: var(--primary);
      transform: scaleX(0);
      transition: transform 0.2s;
    }
    
    &:hover {
      color: var(--primary);
      // background-color: var(--surface-hover);

      &::after {
        transform: scaleX(0.5);
      }
    }
    
    &.active {
      color: var(--primary);
      
      &::after {
        transform: scaleX(1);
      }
    }
    
    i {
      font-size: 0.75rem;
    }
  }

  .nav-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    width: 240px;
    background-color: var(--surface-card);
    border-radius: 8px;
    box-shadow: 0 4px 12px var(--shadow-color);
    border: 1px solid var(--surface-border);
    overflow: hidden;
    z-index: 100;
    transform-origin: top center;
    animation: dropdownEnter 0.2s ease forwards;
  }

  @keyframes dropdownEnter {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .menu-header {
    padding: 0.75rem 1rem;
    color: var(--text-secondary);
    font-size: 0.875rem;
    font-weight: 500;
  }

  .menu-divider {
    height: 1px;
    background-color: var(--surface-border);
    margin: 0.25rem 0;
  }

  .menu-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    color: var(--text-color);
    text-decoration: none;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    .menu-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      color: var(--primary);
      text-align: center;
    }
  }

  .logout-item {
    color: var(--secondary);
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    cursor: pointer;
    
    .menu-icon {
      color: var(--secondary);
    }
    
    &:hover {
      background-color: rgba(220, 38, 38, 0.1);
    }
  }

  /* User Dropdown */
  .user-dropdown-trigger {
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 50%;
    border: none;
    background: none;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    &.active {
      border: 2px solid var(--primary);
    }
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--surface-hover);
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    i {
      color: var(--primary);
      font-size: 1rem;
    }
  }

  .user-dropdown-menu {
    width: 280px;
    right: 0;
    left: auto;
  }

  .user-info {
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .user-avatar-large {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--surface-hover);
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    i {
      color: var(--primary);
      font-size: 1.25rem;
    }
  }

  .user-details {
    overflow: hidden;
  }

  .user-name {
    font-weight: 500;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-email {
    font-size: 0.875rem;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Sign In Button */
  .sign-in-btn {
    height: 38px;
    padding: 0 1.50rem;
    color: var(--ameritrust-primary);
    font-family: Inter;
    border-radius: 10px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    margin-left: 0.5rem;
    transition: all 0.2 ease-in-out;

    &[data-theme='dark'] {
      color: white !important;
    }

    &:hover {
      opacity: 0.9;
    }
  }

  /* Theme Toggle */
  .theme-toggle-btn {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: none;
    background: none;
    color: var(--text-color);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-left: 0.5rem;

    body:not(.login-page):not(.forgot-password-page) & {
      transition: all 0.2s;
    }

    &:hover {
      background-color: var(--surface-hover);
    }
    
    i {
      font-size: 1.125rem;
    }
  }

  /* Mobile Navigation Styles */
  .mobile-nav {
    display: none;
    align-items: center;

    @media (max-width: 950px) {
      display: flex;
    }
  }

  .mobile-logo {
    display: flex;
    top: 1rem;
    left: 1.25rem !important;
    position: absolute;

    .mobile-light-logo, .mobile-dark-logo {
      display: block;
      height: 40px;
    }
  }

  .mobile-menu-btn {
    width: 40px;
    height: 40px;
    z-index: 10000;
    border-radius: 50%;
    border: none;
    background: none;
    color: var(--text-color);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    @media screen and (max-width: 950px) {
      margin-top: -2.5rem;
    }
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    i {
      font-size: 1.25rem;
    }
  }

  main {
    flex: 1;
    width: 100%;
    height: calc(100vh - 64px); /* Viewport height minus navbar height */
    overflow-y: auto;
    position: relative;
    z-index: 1;
  }

  .footer {
    background: var(--surface-card);
    color: var(--text-color);
    padding: 3rem 0 2rem;
    margin-top: auto;
    border-top: 1px solid var(--surface-border);

    .footer-content {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 2rem;
    }

    .footer-main {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 3rem;
      margin-bottom: 2rem;
    }

    .footer-col {
      h4 {
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 1.25rem;
        color: var(--primary);
      }

      .footer-logo {
        height: 40px;
        margin: 0;
        padding: 0;
        display: block;
        line-height: 0;
      }

      .address {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: var(--text-secondary);
        line-height: 1.2;
        margin: 0;
        padding: 0;
        position: relative;
        top: -4px;
        margin-top: -2px;
        transform: translateY(-1px);

        i {
          color: var(--primary);
          margin-top: 0.25rem;
        }
      }

      .contact-links {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        a {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--text-secondary);
          text-decoration: none;
          font-size: 0.875rem;
          transition: color 0.2s;

          i {
            color: var(--primary);
          }

          &:hover {
            color: var(--primary);
          }
        }
      }

      .license-items {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        .license-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: var(--text-secondary);

          i {
            color: var(--primary);
          }
        }
      }
    }

    .footer-divider {
      height: 1px;
      background: var(--surface-border);
      margin: 2rem 0;
    }

    .footer-legal {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .legal-left {
        display: flex;
        align-items: center;
        gap: 1.5rem;

        .equal-housing-logo {
          height: 32px;
        }

        .nmls-link {
          color: var(--primary);
          text-decoration: none;
          font-size: 0.875rem;
          font-weight: 500;

          &:hover {
            text-decoration: underline;
          }
        }
      }

      .copyright {
        font-size: 0.875rem;
        color: var(--text-secondary);
      }
    }
  }

  @media (max-width: 1024px) {
    .footer {
      .footer-main {
        grid-template-columns: 1fr;
        gap: 2rem;
        text-align: center;
      }

      .footer-col {
        .footer-logo {
          margin: 0 auto 1.25rem;
        }

        .address {
          justify-content: center;
        }

        .contact-links a {
          justify-content: center;
        }

        .license-items .license-item {
          justify-content: center;
        }
      }

      .footer-legal {
        flex-direction: column;
        gap: 1rem;
        text-align: center;

        .legal-left {
          flex-direction: column;
          gap: 1rem;
        }
      }
    }
  }

  @media (max-width: 768px) {
    body {
      margin-top: 64px;
    }

    .navbar {
      top: 0;
      padding: 0.75rem 1rem;

      .nav-links {
        display: none; /* Will add hamburger menu */
      }
    }

    .footer {
      padding: 2rem 0 1.5rem;

      .footer-content {
        padding: 0 1rem;
      }
    }
  }

  .v-application {
    background-color: var(--background-color);
  }

  .page-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 16px;
  }

  .snackbar {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    z-index: 9999;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    min-width: 300px;
    max-width: 500px;
    animation: slideIn 0.3s ease;
    &.success {
      background: #10b981;
      color: white;
    }
    &.error {
      background: #ef4444;
      color: white;
    }
    &.warning {
      background: #f59e0b;
      color: white;
    }
    &.info {
      background: #3b82f6;
      color: white;
    }

    .snackbar-icon {
      font-size: 1.25rem;
    }

    .close-btn {
      margin-left: auto;
      background: none;
      border: none;
      color: currentColor;
      opacity: 0.8;
      cursor: pointer;
      padding: 0.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 1;
      }
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }

  @keyframes slideIn {
    from {
      transform: translateY(1rem);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .theme-toggle {
    background: none;
    border: none;
    color: var(--text-color);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    margin: 0 0 0 0.25rem;
    padding: 0;
    box-sizing: border-box;

    &:hover {
      background: var(--surface-hover);
    }

    i {
      font-size: 1.125rem;
    }
  }

  .profile-dropdown {
    background: none;
    border: none;
    color: var(--text-color);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    margin: 0;
    padding: 0;
    box-sizing: border-box;

    &:hover {
      background: var(--surface-hover);
    }

    i {
      font-size: 1.125rem;
    }
  }

  :root[data-theme='dark'] {
    .navbar .logo, .mobile-menu-panel .mobile-logo {
      img[src*='ameritrusttpo.png'] {
        display: none;
      }

      .dark-logo {
        display: block;
      }
    }

    .footer {
      .address,
      .contact-links a,
      .license-items .license-item,
      .copyright {
        color: var(--text-secondary);
      }

      .address {
        margin-top: 2rem;
      }

      .footer-col h4 {
        color: #fff;
      }

      .contact-links a:hover {
        color: #fff;
      }

      .nmls-link {
        color: #fff;

        &:hover {
          opacity: 0.9;
        }
      }
    }

    .navbar .logo,
    .footer-col {
      .light-logo {
        display: none;
      }

      .dark-logo {
        display: block;
      }
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--surface-border);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .custom-dropdown {
    position: relative;
    display: inline-block;
  }

  .dropdown-arrow {
    margin-left: 5px;
    font-size: 12px;
  }

  .dropdown-menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 8px 0;
    margin-top: 4px;
    min-width: 150px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }

  .dropdown-menu.show {
    display: block;
  }

  .dropdown-item {
    display: block;
    padding: 8px 16px;
    color: #333;
    text-decoration: none;
  }

  .dropdown-item:hover {
    background-color: #f5f5f5;
  }

  // Update mobile menu button styles
  .mobile-menu-btn {
    @apply hidden;
    color: var(--text-color);
    padding: 0.5rem;
    margin-left: 0.5rem;
    @media (max-width: 950px) {
      @apply flex items-center justify-center;
    }
  }

  // Update existing navbar styles
  .navbar {
    position: relative;
    .nav-links {
      @media (max-width: 950px) {
        position: absolute;
        top: 100%;
        right: 0;
        width: 240px;
        background: var(--background-color);
        padding: 1rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
        z-index: 50;
        border-radius: 0.5rem;
        margin-top: 0.5rem;

        &.mobile-active {
          display: flex;
        }

        .nav-link {
          width: 100%;
          padding: 0.5rem 0;
        }
      }

      @media (min-width: 951px) {
        display: flex;
        align-items: center;
      }
    }
  }

  // Update transitions and animations
  @keyframes rotate-in {
    0% {
      transform: rotate(0deg) scale(1);
    }
    50% {
      transform: rotate(180deg) scale(1.2);
    }
    100% {
      transform: rotate(360deg) scale(1);
    }
  }

  @keyframes rotate-out {
    0% {
      transform: rotate(360deg) scale(1);
    }
    50% {
      transform: rotate(180deg) scale(0.8);
    }
    100% {
      transform: rotate(0deg) scale(1);
    }
  }

  .rotate-in {
    animation: rotate-in 0.4s ease-out forwards;
  }

  .rotate-out {
    animation: rotate-out 0.4s ease-out forwards;
  }

  // Update mobile menu styles
  .mobile-menu {
    @apply backdrop-blur-lg bg-white/90 dark:bg-gray-900/90;
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    .nav-link {
      @apply transition-all duration-200;

      &:hover {
        @apply translate-x-2;
        text-shadow: 0 0 20px rgba(0, 113, 227, 0.3);
      }

      i {
        @apply transition-all duration-200;
      }

      &:hover i {
        @apply scale-110 text-[#0071e3];
        filter: drop-shadow(0 0 8px rgba(0, 113, 227, 0.4));
      }
    }
  }

  // Update mobile menu button
  .mobile-menu-btn {
    @apply transition-transform duration-200;
    &:hover {
      @apply scale-110;
    }
    &:active {
      @apply scale-95;
    }
    i {
      @apply transition-all duration-300;
      transform-origin: center;
    }
  }

  // Update keyframe animations
  @keyframes slideIn {
    0% {
      opacity: 0;
      visibility: visible;
      transform: translateY(-20px) scale(0.95);
    }
    100% {
      opacity: 1;
      visibility: visible;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes slideOut {
    0% {
      opacity: 1;
      visibility: visible;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      visibility: hidden;
      transform: translateY(-20px) scale(0.95);
    }
  }

  // Update animation classes
  .animate-in {
    animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  .animate-out {
    animation: slideOut 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards;
  }

  // Separate mobile and desktop nav styles
  .nav-links {
    // Desktop styles
  }

  .nav-links-mobile {
    position: absolute;
    top: 100%;
    right: 0;
    width: 200px;
    background: var(--background-color);
    padding: 0.75rem 0.75rem;
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
    z-index: 50;
    border-radius: 0.5rem;
    margin-top: 0.5rem;
    display: flex;
    transform-origin: top right;
    backdrop-filter: blur(8px);
    
    .nav-link {
      width: 100%;
      transition: all 0.2s ease;
      text-align: right;
      padding: 0.5rem 0.75rem;
      font-size: 1rem;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%) scaleX(0);
        width: 3px;
        height: 70%;
        background-color: var(--primary);
        transition: transform 0.2s ease;
        border-radius: 2px;
        transform-origin: right;
      }

      &:hover {
        transform: translateX(-4px);
      }

      &.router-link-active {
        color: var(--primary) !important;
        background: var(--surface-hover);
        
        &::after {
          transform: translateY(-50%) scaleX(1);
        }
      }

      &.flex {
        flex-direction: row-reverse;
        justify-content: flex-start;

        i {
          margin-right: 0;
          margin-left: 0.5rem;
          font-size: 1rem;
        }
      }
    }
    .dropdown-menu {
      position: relative !important;
      right: auto !important;
      left: 0 !important;
      width: 90% !important;
      margin-right: auto;
      box-shadow: none;
      background: transparent;
      padding-right: 0;

      .nav-link {
        padding-left: 2rem;
        text-align: left;

        &:hover {
          transform: translateX(4px);
        }

        &.flex {
          flex-direction: row;

          i {
            margin-right: 0.5rem;
            margin-left: 0;
          }
        }
      }
    }
  }

  /* Add these new styles */
  .nav-item {
    position: relative;
    display: inline-flex;
    height: 100%;
    align-items: center;
  }

  .nav-links {
    .nav-item .nav-link {
      padding: 0.5rem 1rem;
      height: 100%;
      display: flex;
      align-items: center;
    }
  }

  /* Mobile nav item styles */
  .nav-item-mobile {
    position: relative;
    width: 100%;

    .nav-link {
      height: auto;
      padding: 0.75rem 1rem;
      width: 100%;
    }
  }

  /* New stable dropdown styles */
  .stable-dropdown {
    position: relative;
    display: inline-flex;
    align-items: center;
    margin: 0;
    height: 38px;
    box-sizing: border-box;
    width: auto;
    /* Fix width to prevent layout shifts */
    min-width: 107px;
  }

  .company-dropdown-btn {
    background: transparent;
    color: var(--text-color);
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.9375rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 38px;
    width: 100%;
    box-sizing: border-box;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.2s;
    line-height: 1.5;

    /* Prevent text from wrapping */
    text-align: center;
    overflow: visible;

    &:hover {
      background: var(--surface-hover);
    }

    &[data-state="open"] {
      background: var(--surface-hover);
    }

    span {
      display: inline-block;
      position: relative;
      margin-right: 0.5rem; /* Fixed spacing between text and icon */
    }

    .icon-chevron {
      font-size: 0.7rem;
      transition: transform 0.2s ease;
      display: inline-flex;
      flex: 0 0 auto;
      /* Fixed width for icon to prevent layout shifts */
      width: 8px;
      text-align: center;
    }

    .icon-chevron.rotate {
      transform: rotate(180deg);
    }
  }

  .dropdown-content {
    position: fixed;
    width: 14rem;
    background: var(--surface-card);
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--surface-border);
    padding: 0.25rem;
    margin-top: 0.5rem;
    z-index: 100;
    box-sizing: border-box;
  }

  .dropdown-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--text-secondary);
    padding: 0.625rem 0.75rem;
  }

  .dropdown-separator {
    height: 1px;
    background-color: var(--surface-border);
    margin: 0.25rem 0;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    padding: 0.625rem 0.75rem;
    border-radius: 0.5rem;
    transition: background-color 0.15s;
    cursor: pointer;
    font-size: 0.875rem;
    
    &:hover {
      background-color: var(--surface-hover);
    }

    .item-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      font-size: 1rem;
      color: var(--primary);
      text-align: center;
    }
  }

  /* Mobile stable dropdown styles */
  .mobile-stable-dropdown {
    position: relative;
    width: 100%;
    margin-bottom: 0.25rem;
  }

  .mobile-company-dropdown-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    text-align: right;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    color: #000;
    font-size: 1rem;
    font-weight: 500;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    .icon-chevron {
      font-size: 0.7rem;
      transition: transform 0.2s ease;
    }

    .icon-chevron.rotate {
      transform: rotate(180deg);
    }
  }

  .mobile-dropdown-content {
    position: absolute;
    top: auto;
    right: auto;
    width: 14rem;
    margin-top: 0.5rem;
    background: white;
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(0, 0, 0, 0.1);
    z-index: 100;
  }

  .mobile-dropdown-inner {
    padding: 0.5rem;
  }

  .mobile-dropdown-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    color: #1d1d1f;
    text-decoration: none;
    transition: background-color 0.15s;
    font-size: 0.875rem;
    
    &:hover {
      background-color: #f5f5f7;
    }

    .item-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      font-size: 1rem;
      color: #0071e3;
      text-align: center;
    }
  }

  /* Company dropdown container to ensure consistent spacing */
  .company-container {
    display: flex;
    align-items: center;
    height: 38px;
    padding: 0;
    margin: 0 0.35rem;
    box-sizing: border-box;
    position: relative;
  }

  /* Update dropdown behavior when opened */
  .nav-item.company-container {
    position: static;
  }

  /* Ensure dropdown stays in place when open */
  [data-state="open"] {
    position: relative;
    z-index: 101;
  }

  /* Position fixed for all dropdown content */
  [data-radix-popper-content-wrapper] {
    position: fixed !important;
  }

  /* Mobile Navigation Overlay */
  .mobile-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9998;
    animation: fadeIn 0.2s ease forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Mobile Menu Panel */
  .mobile-menu-panel {
    position: fixed;
    // top: 64px;
    top: 0;
    padding-top: 70px !important;
    right: 0;
    width: 300px;
    max-width: 100%;
    height: calc(100vh);
    background-color: var(--surface-card);
    border-bottom-left-radius: 1rem;
    z-index: 9999;
    padding: 1rem;
    overflow-y: auto;
    box-shadow: -2px 0 10px var(--shadow-color);
    transform: translateX(100%);
    transition: transform 0.3s ease;
    
    &.open {
      transform: translateX(0);
    }
  }

  .mobile-menu-item {
    display: flex;
    align-items: center;
    padding: 0.875rem 1rem;
    color: var(--text-color);
    text-decoration: none;
    font-weight: 500;
    border-radius: 8px;
    transition: background-color 0.2s;
    margin-bottom: 0.25rem;
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    .menu-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      color: var(--primary);
      text-align: center;
    }
    
    &.router-link-active {
      color: var(--primary);
      background-color: var(--surface-hover);
    }
  }

  .mobile-dropdown {
    margin-bottom: 0.25rem;
  }

  .mobile-dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.875rem 1rem;
    color: var(--text-color);
    background: none;
    border: none;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    border-radius: 8px;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    i {
      transition: transform 0.2s;
    }
  }

  .mobile-dropdown-content {
    // padding-left: 1rem;
    background-color: var(--surface-ground);
    border: 1px solid var(--primary-hover);
    margin-top: 0.25rem;
    animation: slideDown 0.2s ease forwards;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .mobile-menu-subitem {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    color: var(--text-color);
    text-decoration: none;
    border-radius: 8px;
    transition: background-color 0.2s;
    margin-bottom: 0.25rem;
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    .menu-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      color: var(--primary);
      text-align: center;
    }
    
    &.router-link-active {
      color: var(--primary);
      background-color: var(--surface-card);
    }
  }

  .mobile-menu-divider {
    height: 1px;
    background-color: var(--surface-border);
    margin: 1rem 0;
  }

  .mobile-user-section {
    margin-bottom: 1rem;
  }

  .mobile-user-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    margin-bottom: 1rem;
    background-color: var(--surface-hover);
    border-radius: 8px;
  }

  .mobile-user-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--surface-card);
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    i {
      color: var(--primary);
      font-size: 1.25rem;
    }
  }

  .mobile-user-details {
    overflow: hidden;
    flex: 1;
  }

  .mobile-user-name {
    color: var(--text-color);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mobile-user-email {
    font-size: 0.875rem;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mobile-logout-btn {
    color: var(--secondary);
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    cursor: pointer;
    
    .menu-icon {
      color: var(--secondary);
    }
    
    &:hover {
      background-color: rgba(219, 86, 86, 0.1);
    }
  }

  .mobile-sign-in {
    background-color: var(--ameritrust-primary) !important;
    color: white !important;
  }

  .mobile-theme-toggle {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.875rem 1rem;
    margin-top: 1rem;
    background: none;
    border: none;
    color: var(--text-color);
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    border-radius: 8px;

    body:not(.login-page):not(.forgot-password-page) & {
      transition: background-color 0.2s;
    }

    &:hover {
      background-color: var(--surface-hover);
    }
    
    i {
      width: 1.25rem;
      margin-right: 0.75rem;
      text-align: center;
    }
  }

  /* Responsiveness */
  @media (max-width: 768px) {
    .navbar {
      position: sticky;
    }
  }

  /* Utility classes */
  .ml-2 {
    margin-left: 0.5rem;
  }

  .rotate-180 {
    transform: rotate(180deg);
  }

  /* Transitions */
  .transition-transform {
    transition: transform 0.2s ease;
  }

  /* Dark mode overrides */
  :root[data-theme='dark'] {
    .navbar .logo, .mobile-menu-panel .mobile-logo {
      img[src*='ameritrusttpo.png'] {
        display: none;
      }

      .dark-logo {
        display: block;
      }

      .mobile-dark-logo {
        display: block;
      }

      .mobile-light-logo {
        display: none;
      }
    }

    .fallback-avatar {
      background-color: rgba(59, 130, 246, 0.2) !important;
      
      i {
        color: #3b82f6 !important;
      }
    }
  }

  /* Define primary RGB values for rgba usage */
  :root {
    --primary-rgb: 0, 31, 96; /* #001f60 */
  }

  :root[data-theme='dark'] {
    --primary-rgb: 59, 130, 246; /* #3b82f6 */
  }

  /* Retain existing styles below this line */
  main {
    flex: 1;
    width: 100%;
    height: calc(100vh - 64px);
    overflow-y: auto;
    position: relative;
    z-index: 1;
  }

  // ... keep existing CSS for footer, etc ...

  .coming-soon-badge {
    background: none;
    color: #b0b8c1;
    font-size: 0.68rem;
    font-weight: 600;
    border-radius: 0;
    padding: 0;
    margin-left: 0;
    margin-top: 0.1em;
    letter-spacing: 0.01em;
    display: block;
    vertical-align: baseline;
    border: none;
    text-align: left;
  }
  .coming-soon-nav {
    cursor: not-allowed !important;
    user-select: none;
  }
</style>